import React from 'react';

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function normaliseSchedule(raw) {
  if (!raw) return null;
  if (typeof raw === 'object' && raw !== null) return raw;
  if (raw === 'triggered')      return { type: 'triggered', days: [1,2,3,4,5], from: '09:00', to: '17:00' };
  if (raw === 'business_hours') return { type: 'custom',    days: [1,2,3,4,5], from: '09:00', to: '17:00' };
  if (raw === 'always')         return { type: 'always',    days: [1,2,3,4,5,6,7], from: '00:00', to: '23:59' };
  return null;
}

export function scheduleLabel(cfg) {
  if (!cfg) return 'No schedule';
  const c = normaliseSchedule(cfg);
  if (!c || c.type === 'triggered') return 'Triggered only';
  if (c.type === 'always') return '24/7 — always on';
  const days = (c.days || [1,2,3,4,5]).map(d => DAY_NAMES[d]).join(', ');
  return `${days} · ${c.from || '09:00'} – ${c.to || '17:00'}`;
}

export function ScheduleEditor({ value, onChange, allowNull = true }) {
  const cfg = normaliseSchedule(value) || { type: 'triggered', days: [1,2,3,4,5], from: '09:00', to: '17:00' };
  const type = cfg.type || 'triggered';

  const setType = (t) => {
    if (t === 'triggered') {
      onChange(allowNull ? null : { type: 'triggered', days: [1,2,3,4,5], from: '09:00', to: '17:00' });
    } else if (t === 'always') {
      onChange({ type: 'always', days: [1,2,3,4,5,6,7], from: '00:00', to: '23:59' });
    } else {
      onChange({ ...cfg, type: 'custom', days: cfg.days?.length ? cfg.days : [1,2,3,4,5] });
    }
  };

  const toggleDay = (d) => {
    const days = cfg.days || [1,2,3,4,5];
    const next = days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort((a, b) => a - b);
    onChange({ ...cfg, days: next.length ? next : [d] });
  };

  return (
    <div className="schedule-editor">
      <div className="sched-type-row">
        {[
          { id: 'triggered', label: 'Triggered only' },
          { id: 'custom',    label: 'Custom schedule' },
          { id: 'always',    label: '24/7' },
        ].map(opt => (
          <button
            key={opt.id}
            className={`sched-type-btn${type === opt.id ? ' active' : ''}`}
            onClick={() => setType(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {type === 'custom' && (
        <>
          <div className="sched-days-row">
            {[[1,'Mon'],[2,'Tue'],[3,'Wed'],[4,'Thu'],[5,'Fri'],[6,'Sat'],[7,'Sun']].map(([d, name]) => (
              <button
                key={d}
                className={`sched-day-btn${(cfg.days || []).includes(d) ? ' active' : ''}`}
                onClick={() => toggleDay(d)}
              >
                {name}
              </button>
            ))}
          </div>
          <div className="sched-time-row">
            <label className="sched-time-label">From</label>
            <input
              type="time"
              className="sched-time-input"
              value={cfg.from || '09:00'}
              onChange={e => onChange({ ...cfg, from: e.target.value })}
            />
            <label className="sched-time-label">To</label>
            <input
              type="time"
              className="sched-time-input"
              value={cfg.to || '17:00'}
              onChange={e => onChange({ ...cfg, to: e.target.value })}
            />
          </div>
        </>
      )}
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <span className={`exec-toggle${on ? ' on' : ''}`} onClick={() => onChange(!on)} style={{ cursor: 'pointer' }}>
      <span className="exec-toggle-knob" />
    </span>
  );
}

export default function ExecutionPanel({
  nodes, pipelineActive, pipelineSchedule,
  onTogglePipeline, onSetPipelineSchedule,
  onUpdateNode, onClose, opsMode,
}) {
  const functionNodes = nodes.filter(n => n.type === 'functionNode');
  const activeCount   = functionNodes.filter(n => n.data.active !== false).length;

  return (
    <div className="exec-panel">
      <div className="exec-panel-hdr">
        <span className="exec-panel-icon">⚡</span>
        <span className="exec-panel-title">Execution &amp; Schedules</span>
        <button className="exec-panel-close" aria-label="Close dialog" title="Close" onClick={onClose}>×</button>
      </div>

      <div className="exec-panel-body">

        {/* ── Pipeline status ── */}
        <div className="exec-section">
          <div className="exec-section-title">Pipeline status</div>
          <div className="exec-status-row">
            <div className={`exec-status-dot${pipelineActive ? ' on' : ''}`} />
            <span className="exec-status-text">
              {pipelineActive
                ? `Running — ${activeCount} of ${functionNodes.length} ${opsMode ? 'department' : 'function'}${functionNodes.length !== 1 ? 's' : ''} active`
                : `Inactive — no ${opsMode ? 'departments' : 'functions'} running`}
            </span>
          </div>
          <div className="exec-actions-row">
            <button
              className={`exec-run-btn${pipelineActive ? ' stop' : ''}`}
              onClick={onTogglePipeline}
            >
              {pipelineActive ? '⏹ Stop all' : '▶ Run all'}
            </button>
          </div>
        </div>

        {/* ── Global schedule ── */}
        <div className="exec-section">
          <div className="exec-section-title">Global schedule</div>
          <div className="exec-section-desc">
            Default for all {opsMode ? 'departments' : 'functions'} unless individually overridden.
          </div>
          <ScheduleEditor
            value={pipelineSchedule}
            onChange={onSetPipelineSchedule}
            allowNull={false}
          />
        </div>

        {/* ── Per-function overrides ── */}
        <div className="exec-section">
          <div className="exec-section-title">{opsMode ? 'Department' : 'Function'} overrides</div>
          <div className="exec-section-desc">
            Toggle individual {opsMode ? 'departments' : 'functions'} or set a different schedule.
          </div>

          {functionNodes.length === 0 && (
            <div className="exec-empty">No {opsMode ? 'departments' : 'functions'} on the canvas yet.</div>
          )}

          {functionNodes.map(node => {
            const active     = node.data.active !== false;
            const rawSched   = node.data.scheduleConfig != null ? node.data.scheduleConfig : node.data.schedule;
            const schedCfg   = normaliseSchedule(rawSched);
            const hasOverride = schedCfg !== null;

            return (
              <div key={node.id} className={`exec-fn-row${active ? '' : ' inactive'}`}>
                <div className="exec-fn-top">
                  <span className="exec-fn-icon">{opsMode ? '🏢' : '⬡'}</span>
                  <span className="exec-fn-name">{node.data.label || 'Unnamed'}</span>
                  <Toggle
                    on={active}
                    onChange={(v) => onUpdateNode(node.id, { active: v })}
                  />
                </div>

                {active && (
                  <div className="exec-fn-schedule">
                    <ScheduleEditor
                      value={schedCfg}
                      onChange={(v) => onUpdateNode(node.id, { scheduleConfig: v, schedule: null })}
                      allowNull={true}
                    />
                    <div className="exec-fn-tag">
                      {hasOverride
                        ? <>
                            <span className="exec-override-tag">↳ overrides global</span>
                            <button
                              className="exec-fn-clear-btn"
                              title="Remove override"
                              onClick={() => onUpdateNode(node.id, { scheduleConfig: null, schedule: null })}
                            >
                              reset ✕
                            </button>
                          </>
                        : <span className="exec-follows-tag">↳ follows global schedule</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
